import * as React from "react";
import Box from "@mui/material/Box";
import { TextField, type ITextFieldStyles } from "@fluentui/react/lib/TextField";
import { TooltipHost } from "@fluentui/react";
import { Info } from "lucide-react";
import { LTLState } from "@taskpane/hooks/useLTLReducer";
import { ltlFieldInputId } from "@taskpane/utils/ltlValidationFocus";
import {
  ltlWeightLimitWarning,
  totalLtlItemWeight,
} from "@taskpane/utils/ltlQuotingLimits";
import { itemsEditorStyles as styles } from "./itemsEditor.styles";

type ItemRow = LTLState["items"][number];

interface Props {
  item: ItemRow;
  index: number;
  state: LTLState;
  onItemFieldChange: (index: number, field: keyof ItemRow, value: unknown) => void;
  onFieldBlur?: () => void;
  getTextFieldStyles: (fieldKey: string) => unknown;
  maxWeight?: number;
}

const WEIGHT_WARN_ORANGE = "#d97706";

export function LtlFreightItemClassWeightPiecesRow({
  item,
  index,
  state,
  onItemFieldChange,
  onFieldBlur,
  getTextFieldStyles,
  maxWeight,
}: Props): React.ReactElement {
  const st = (suffix: string) => getTextFieldStyles(`item${index}_${suffix}`) as ITextFieldStyles;
  const weightWarning = ltlWeightLimitWarning(totalLtlItemWeight(state.items), maxWeight);

  return (
    <Box sx={styles.row}>
      <Box sx={styles.fieldGroup}>
        <TextField
          label="Class"
          value={item.class}
          onChange={(e, newValue) => onItemFieldChange(index, "class", newValue || "")}
          onBlur={onFieldBlur}
          required
          id={ltlFieldInputId(`item${index}_class`)}
          styles={st("class")}
          errorMessage={state.errors[`item${index}_class`]}
        />
      </Box>
      <Box sx={styles.fieldGroup}>
        <TextField
          label="Weight (lbs)"
          type="number"
          value={String(item.weight)}
          onChange={(e, newValue) =>
            onItemFieldChange(index, "weight", parseFloat(newValue || "0"))
          }
          onBlur={onFieldBlur}
          required
          id={ltlFieldInputId(`item${index}_weight`)}
          styles={st("weight")}
          errorMessage={state.errors[`item${index}_weight`]}
          onRenderLabel={(props, defaultRender) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginBottom: "4px",
              }}
            >
              {defaultRender?.(props)}
              {weightWarning ? (
                <TooltipHost content={weightWarning}>
                  <Box
                    component="span"
                    role="img"
                    aria-label={weightWarning}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                      color: WEIGHT_WARN_ORANGE,
                    }}
                  >
                    <Info size={14} color={WEIGHT_WARN_ORANGE} strokeWidth={2.25} />
                  </Box>
                </TooltipHost>
              ) : null}
            </Box>
          )}
        />
      </Box>
      <Box sx={styles.fieldGroup}>
        <TextField
          label="Pieces"
          value={item.pieces}
          id={ltlFieldInputId(`item${index}_pieces`)}
          styles={st("pieces")}
          onChange={(e, newValue) => onItemFieldChange(index, "pieces", newValue || "Pieces")}
          onBlur={onFieldBlur}
        />
      </Box>
      <Box sx={styles.fieldGroup}>
        <TextField
          label="Pieces Count"
          type="number"
          value={String(item.piecesCount)}
          id={ltlFieldInputId(`item${index}_piecesCount`)}
          styles={st("piecesCount")}
          onChange={(e, newValue) =>
            onItemFieldChange(index, "piecesCount", parseInt(newValue || "1", 10))
          }
          onBlur={onFieldBlur}
        />
      </Box>
    </Box>
  );
}
